import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api, { API } from "@/lib/api";
import { FileText, Download, Lock } from "lucide-react";

const CONTENTS = [
  "Executive Overview", "Methodology", "Verified listings analyzed",
  "Participant experiences collected", "Statewide screening trends",
  "County-by-county comparisons", "Credit score requirements",
  "Income multiplier requirements", "Reserve requirements", "Cosigner policies",
  "Application fee trends", "Transparency & communication findings",
  "Research findings", "Data limitations", "Appendix",
];

function ProgressMetric({ label, value, target, testid }) {
  const pct = Math.min(100, Math.round((value / Math.max(1, target)) * 100));
  return (
    <div data-testid={testid}>
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="text-sm text-slate-700">{label}</span>
        <span className="font-mono text-xs text-slate-500">
          {value.toLocaleString()} / {target.toLocaleString()}
        </span>
      </div>
      <div className="h-2 w-full bg-slate-100">
        <div className="h-2 bg-calblue transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function ReportPage() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/report/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  const year = status?.year || new Date().getFullYear();
  const version = status?.version || "1.0";
  const available = status?.available;
  const citation = `California Rental Experience Project. (${year}). California Rental Screening Report ${year} (Version ${version}). California Rental Experience Project.`;

  return (
    <div className="min-h-screen bg-white font-body text-ink">
      <Header />

      <section className="mx-auto max-w-7xl px-6 pb-12 pt-20 md:px-12 md:pt-28">
        <div className="text-xs uppercase tracking-[0.2em] text-calblue">
          Annual Research Publication
        </div>
        <h1 className="mt-4 max-w-4xl font-serif text-5xl font-medium leading-[1.05] tracking-tight text-ink md:text-6xl">
          The California Rental Screening Report
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-700">
          The California Rental Screening Report summarizes verified, anonymized rental
          screening criteria and participant experiences collected across California. Our goal
          is to improve understanding through data—not advocacy.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-32 md:px-12">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* left: action */}
          <div className="lg:col-span-5">
            <div className="border border-slate-200 bg-white p-8">
              <FileText className="h-10 w-10 text-navy" />
              <div className="mt-5 font-serif text-3xl font-medium text-ink">{year} Edition</div>
              <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">
                Version {version}
              </div>

              {status === null ? (
                <p className="mt-4 text-slate-500">Loading…</p>
              ) : available ? (
                <>
                  <p className="mt-3 text-slate-600">
                    The {year} report is available as a print-ready PDF in legislative briefing
                    format.
                  </p>
                  <a
                    href={`${API}/report/annual`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="download-report-button"
                    className="mt-6 inline-flex items-center rounded-none bg-navy px-8 py-4 text-base font-medium text-white transition-colors hover:bg-navy-hover"
                  >
                    <Download className="mr-2 h-5 w-5" /> Download the {year} Report (PDF)
                  </a>
                </>
              ) : (
                <div data-testid="report-unavailable">
                  <div className="mt-3 flex items-center gap-2 text-slate-600">
                    <Lock className="h-4 w-4" /> In preparation
                  </div>
                  <p className="mt-3 text-slate-600">
                    The first edition will be published once enough listings have been
                    independently verified to ensure the findings are statistically meaningful.
                    We will not publish early.
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">
                    The California Rental Experience Project is committed to publishing only
                    findings supported by sufficient verified data and transparent methodology.
                  </p>

                  <div
                    data-testid="report-progress-metrics"
                    className="mt-7 space-y-5 border-t border-slate-100 pt-6"
                  >
                    <ProgressMetric
                      label="Verified Listings"
                      value={status.verified_listings || 0}
                      target={status.min_verified_listings || 50}
                      testid="metric-verified-listings"
                    />
                    <ProgressMetric
                      label="Experiences Collected"
                      value={status.experiences || 0}
                      target={status.min_experiences || status.min_verified_listings || 50}
                      testid="metric-experiences-collected"
                    />
                    <ProgressMetric
                      label="Counties Represented"
                      value={status.counties_represented || 0}
                      target={58}
                      testid="metric-counties-represented"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* suggested citation */}
            <div
              data-testid="report-citation"
              className="mt-6 border border-calblue/40 bg-slate-50 p-6"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-calblue">
                Suggested Citation
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">{citation}</p>
              <p className="mt-4 text-xs text-slate-400">
                Founded by Ramon Pasos.{" "}
                <Link to="/about" className="text-calblue hover:underline">
                  About the Project
                </Link>
              </p>
            </div>
          </div>

          {/* right: contents */}
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] text-calblue">
              What the report includes
            </div>
            <h2 className="mt-3 font-serif text-3xl font-medium tracking-tight text-ink">
              Inside each edition
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2">
              {CONTENTS.map((c, i) => (
                <div key={c} className="flex items-center gap-3 bg-white p-4 text-sm">
                  <span className="font-mono text-xs text-calblue">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-slate-700">{c}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-slate-500">
              The reporting engine is modular — future editions will add datasets such as
              application transparency, communication timelines, and landlord and tenant
              experiences as the verified data grows.{" "}
              <Link to="/methodology" className="text-calblue hover:underline">
                Read our methodology.
              </Link>{" "}
              <Link to="/research-standards" className="text-calblue hover:underline">
                See our research standards.
              </Link>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
